import {assertAndContinue} from 'Asserts';
import {SequencePrime} from 'biojs/BioConstants';
import {CodonTranslationTable} from 'biojs/BioConstants';
import {Strand} from 'constants/MolbioConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {NucleotideType, SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BulkAssemblyMethod,
  BulkAssemblyOligoSource,
  getMaxFragmentBinCount,
} from 'molbio/bulk-assembly/BulkAssemblyConstants';
import {capitalize} from 'util/NameUtil';
import {assertNever, LOADING} from 'util/Types';

export enum ValidationStatus {
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
}

export interface BulkAssemblyFragmentValidationResult {
  fragmentId: string;
  validationMessages: Array<BulkAssemblyValidationMessage>;
}

export interface BoundPrimer {
  oligoId: string | null;
  oligoSource: BulkAssemblyOligoSource;
  bindPosition: number;
  overhangLength: number;
  bindTm: number;
  wholeTm: number;
  oligoContents: {name: string; bases: string};
}

export interface BulkAssemblyPrimerValidationResult {
  fragmentId: string;
  sequencePrime: SequencePrime;
  primer: BoundPrimer | null;
  validationMessages: Array<BulkAssemblyValidationMessage>;
}

export interface ValidationConstructFragment {
  fragmentId: string;
  start: number;
  end: number;
  overhang3Bases: string | null;
  overlap5End: number | null;
  overlap3Start: number | null;
  inputStart: number | null;
  inputEnd: number | null;
}

export interface AssembledProteinConstruct {
  createdProteinId: string | null;
  createdProteinCurrentName: string | null;
  aminoAcids: string;
  annotations: Array<{
    name: string;
    start: number;
    end: number;
    color: string;
  }>;
}

export interface AssembledSequenceConstruct {
  createdSequenceId: string | null;
  createdSequenceCurrentName: string | null;
  bases: string;
  circular: boolean;
  sequenceType: SequenceType;
  nucleotideType: NucleotideType;
  annotations: Array<{
    name: string;
    start: number;
    end: number;
    color: string;
    strand: Strand;
  }>;
  translations: Array<{
    name: string;
    strand: Strand;
    color: string;
    codonTranslationTable: CodonTranslationTable;
    coerceStartCodonToMethionine: boolean;
    regions: Array<{start: number; end: number}>;
  }>;
  modifiedNucleotides: Array<ModifiedNucleotideAttributes>;
}

export interface BulkAssemblyConstructValidationResult {
  constructId: string;
  polymer: AssembledSequenceConstruct | AssembledProteinConstruct | null;
  constructFragments: Array<ValidationConstructFragment>;
  primerValidationResults: Array<BulkAssemblyPrimerValidationResult>;
  validationMessages: Array<BulkAssemblyValidationMessage>;
}

export interface BulkAsesmblyAssemblyValidationResult {
  validationMessages: Array<BulkAssemblyValidationMessage>;
}

export interface AsyncBulkAssemblyValidationResults {
  assemblyValidationResult: BulkAsesmblyAssemblyValidationResult;
  fragmentValidationResults: Array<BulkAssemblyFragmentValidationResult>;
  constructValidationResults: Array<BulkAssemblyConstructValidationResult>;
}

/**
 * Validation results keyed by fragment/construct for each access. A missing or undefined value
 * means that the fragment or construct is not eligible for validation.
 */
export interface ProcessedAsyncBulkAssemblyValidationResults {
  assemblyResult: {validationMessages: Array<BulkAssemblyValidationMessage>} | LOADING | null;
  fragmentResultsByCid: {[fragmentCid: string]: BulkAssemblyFragmentValidationResult | LOADING | undefined};
  constructResultsByCid: {
    [constructCid: string]: BulkAssemblyConstructValidationResult | LOADING | undefined;
  };
}

export interface BulkAssemblyValidationMessage {
  status: ValidationStatus;
  message: string;
  supportingInfo?: string;
}

export type BulkAssemblyValidationMessages = Array<BulkAssemblyValidationMessage> | LOADING;

export enum PrimerType {
  USE_EXISTING = 'USE_EXISTING',
  CREATE_NEW = 'CREATE_NEW',
}

export interface BaseDisplayPrimer {
  // Name, bases, and melting temp may be missing if no primer could be found or generated.
  name: string | null;
  bases: string | null;
  wholeMeltingTemperature: number | null;
  fragmentName: string;
  orientation: SequencePrime;
  validationMessages: Array<BulkAssemblyValidationMessage>;
}

/**
 * Derived primer information for display in the primers table.
 */
export interface DisplayDesignedPrimer extends BaseDisplayPrimer {
  primerType: PrimerType.CREATE_NEW;
  createdOligoId: string | null;
}

export interface DisplayUserSpecifiedPrimer extends BaseDisplayPrimer {
  primerType: PrimerType.USE_EXISTING;
  oligoId: string | null; // Due to past bugs, the oligoId may be unspecified
}

export type DisplayPrimer = DisplayDesignedPrimer | DisplayUserSpecifiedPrimer;

export enum BulkAssemblyLayoutError {
  ADJACENT_SPACERS = 'ADJACENT_SPACERS',
  DUPLICATE_BIN_NAME = 'DUPLICATE_BIN_NAME',
  SPACERS_NEED_PRIMERS = 'SPACERS_NEED_PRIMERS',
  TERMINAL_LINEAR_SPACERS = 'TERMINAL_LINEAR_SPACERS',
  TOO_MANY_EXISTING_CUTSITES_BINS_FOR_GIBSON = 'TOO_MANY_EXISTING_CUTSITES_BINS_FOR_GIBSON',
  TOO_MANY_BINS = 'TOO_MANY_BINS',
}

export function getErrorText(error: BulkAssemblyLayoutError, bulkAssemblyMethod: BulkAssemblyMethod): string {
  const spacerNameSingular = bulkAssemblyMethod === BulkAssemblyMethod.CONCATENATION ? 'constant' : 'spacer';
  const spacerNamePlural = bulkAssemblyMethod === BulkAssemblyMethod.CONCATENATION ? 'constants' : 'spacers';
  switch (error) {
    case BulkAssemblyLayoutError.ADJACENT_SPACERS:
      return `${capitalize(spacerNamePlural)} cannot be next to one another.`;
    case BulkAssemblyLayoutError.DUPLICATE_BIN_NAME:
      return 'Each bin must have a unique name.';
    case BulkAssemblyLayoutError.SPACERS_NEED_PRIMERS:
      return `${capitalize(spacerNameSingular)} must be next to a bin that uses a primer pair.`;
    case BulkAssemblyLayoutError.TERMINAL_LINEAR_SPACERS:
      return 'Spacers cannot be used before the first bin or after the last bin when assembling linear constructs.';
    case BulkAssemblyLayoutError.TOO_MANY_EXISTING_CUTSITES_BINS_FOR_GIBSON:
      return 'Gibson assemblies can only have one bin that has fragments digested with restriction enzymes.';
    case BulkAssemblyLayoutError.TOO_MANY_BINS:
      return `Assembly cannot have more than ${getMaxFragmentBinCount()} bins. Remove bin(s) to finalize.`;
    default:
      assertAndContinue(false, `Unexpected error type: ${assertNever(error)}.`);
      return '';
  }
}
